import {
  Content,
  menuEditionStatusFactory,
  ProductError,
  ProductErrorType,
  ProductMenuEditionStatus,
  ProductMenuValidationStep,
  SSOLink,
} from '../models';
import { SmartSequenceStyleParams } from './smart-sequence-model';

export class SmartSequenceModelEdited {
  slug: string; // productSlug (as it's the property name send by API, it can not be renamed)
  name: string;
  params: SmartSequenceParamsEdited;
  content: SmartSequenceContentEdited;
  publishedDate?: string;
  updatedDate?: string;

  // for edition
  editionInfo?: SmartSequenceEditionInfo;

  constructor(smartSequenceModel?: SmartSequenceModelEdited) {
    this.slug = smartSequenceModel?.slug;
    this.name = smartSequenceModel?.name;
    this.params = new SmartSequenceParamsEdited(smartSequenceModel?.params);
    this.publishedDate = smartSequenceModel?.publishedDate;
    this.updatedDate = smartSequenceModel?.updatedDate;
    this.editionInfo = new SmartSequenceEditionInfo(smartSequenceModel?.editionInfo);
    this.content = new SmartSequenceContentEdited(smartSequenceModel?.content);
  }
}

export class SmartSequenceContentEdited {
  sequences: SmartSequenceSequenceEdited[];
  intro?: {
    title: string;
    description: string;
    content?: Content;
  };
  outro?: {
    title: string;
    description: string;
    content?: Content;
  };

  constructor(content?: SmartSequenceContentEdited) {
    this.sequences = (content?.sequences || []).map(s => new SmartSequenceSequenceEdited(s));
    this.intro = content?.intro
      ? {
          title: content?.intro?.title,
          description: content?.intro?.description,
          content: new Content(content?.intro?.content),
        }
      : undefined;
    this.outro = content?.outro
      ? {
          title: content?.outro?.title,
          description: content?.outro?.description,
          content: new Content(content?.outro?.content),
        }
      : undefined;
  }

  static validate(data: SmartSequenceContentEdited): ProductError[] {
    const errors: ProductError[] = [];
    if (!data.sequences || data.sequences.length < 1) {
      errors.push({ type: ProductErrorType.NOT_ENOUGH_VALUE, property: 'sequences', payload: {} });
    }
    if (data.intro && (!data.intro.title || !data.intro.title.length)) {
      errors.push({
        type: ProductErrorType.NOT_ENOUGH_VALUE,
        property: 'intro.title',
        payload: {},
      });
    }
    if (data.outro && (!data.outro.title || !data.outro.title.length)) {
      errors.push({
        type: ProductErrorType.NOT_ENOUGH_VALUE,
        property: 'outro.title',
        payload: {},
      });
    }
    if (data.intro && (!data.intro.description || !data.intro.description.length)) {
      errors.push({
        type: ProductErrorType.NOT_ENOUGH_VALUE,
        property: 'intro.description',
        payload: {},
      });
    }
    if (data.outro && (!data.outro.description || !data.outro.description.length)) {
      errors.push({
        type: ProductErrorType.NOT_ENOUGH_VALUE,
        property: 'outro.description',
        payload: {},
      });
    }

    if (data.outro && data.outro.content.type) {
      errors.push(
        ...Content.validate(data.outro.content, 0).map(err => ({ ...err, property: 'outro.' + err.property })),
      );
    }

    if (data.intro && data.intro.content.type) {
      errors.push(
        ...Content.validate(data.intro.content, 0).map(err => ({ ...err, property: 'intro.' + err.property })),
      );
    }

    data.sequences
      .map((s, sequenceIndex) => SmartSequenceSequenceEdited.validate(s, sequenceIndex))
      .forEach(subArray => errors.push(...subArray));
    return errors;
  }
}

export class SmartSequenceSequenceEdited {
  uuid: string;
  title: string;
  description?: string;
  questions: SmartSequenceQuestionEdited[];

  constructor(sequenceSequence?: SmartSequenceSequenceEdited) {
    this.uuid = sequenceSequence?.uuid;
    this.title = sequenceSequence?.title;
    this.description = sequenceSequence?.description;
    this.questions = (sequenceSequence?.questions || []).map(q => new SmartSequenceQuestionEdited(q));
  }

  static validate(data: SmartSequenceSequenceEdited, sequenceIndex: number): ProductError[] {
    const errors: ProductError[] = ProductError.validatePrimitiveTypes(data, ['title']) //
      .map(error => ({ ...error, payload: { sequenceIndex } }));
    if (!data.questions || data.questions.length < 1) {
      errors.push({ type: ProductErrorType.NOT_ENOUGH_VALUE, property: 'questions' });
    }

    const questionsNotLinked = SmartSequenceSequenceEdited.getQuestionsNotLinked(data.questions);

    if (questionsNotLinked.length) {
      errors.push({
        type: ProductErrorType.MISSING,
        property: 'questions',
        payload: { sequenceIndex, questionsNotLinked, code: 'questionUnreachable' },
      });
    }

    if (!SmartSequenceSequenceEdited.smartSequenceHasEnd(data.questions)) {
      errors.push({
        type: ProductErrorType.MISSING,
        property: 'questions',
        payload: { sequenceIndex, code: 'sequenceNeverEnd' },
      });
    }

    data.questions
      .map((q, questionsIndex) => SmartSequenceQuestionEdited.validate(q, sequenceIndex, questionsIndex))
      .forEach(subArray => errors.push(...subArray));

    return errors;
  }

  static findAllRelatedQuestionsToAQuestionId(
    questions: SmartSequenceQuestionEdited[],
    questionUuid: string,
  ): string[] {
    let relatedQuestions = new Set<string>();
    relatedQuestions = SmartSequenceSequenceEdited.dfsQuestionByQuestionId(questions, questionUuid, relatedQuestions);
    return Array.from(relatedQuestions);
  }

  static dfsQuestionByQuestionId(
    questions: SmartSequenceQuestionEdited[],
    questionUuid: string,
    relatedQuestions: Set<string>,
  ): Set<string> {
    let newQuestion = relatedQuestions;
    for (const question of questions) {
      for (const answer of question.answers) {
        if (answer.questionPairedUuid === questionUuid) {
          if (!newQuestion.has(question.uuid)) {
            newQuestion.add(question.uuid);
            newQuestion = SmartSequenceSequenceEdited.dfsQuestionByQuestionId(questions, question.uuid, newQuestion); // Recursive call to explore further relations
          }
        }
      }
    }
    return newQuestion;
  }

  static getQuestionsNotLinked(questions: SmartSequenceQuestionEdited[]): SmartSequenceQuestionEdited[] {
    const answers = questions.map(q => q.answers).flat();
    const results: SmartSequenceQuestionEdited[] = [];
    for (let i = 1; i <= questions.length - 1; i++) {
      const q = questions[i];
      let exist = false;
      for (const a of answers) {
        if (a.questionPairedUuid === q.uuid) {
          exist = true;
          break;
        }
      }
      if (!exist) {
        results.push(q);
      }
    }
    return results;
  }

  static smartSequenceHasEnd(questions: SmartSequenceQuestionEdited[]): boolean {
    return questions
      .map(q => q.answers)
      .flat()
      .some(a => a.questionPairedUuid === null);
  }
}

export class SmartSequenceQuestionEdited {
  uuid: string;
  text: string;
  content: Content;
  skill?: string;
  weight?: number;
  answers: SmartSequenceAnswerEdited[];

  constructor(question?: SmartSequenceQuestionEdited) {
    this.uuid = question?.uuid;
    this.text = question?.text;
    this.content = new Content(question?.content);
    this.skill = question.skill;
    this.weight = question?.weight;
    this.answers = (question?.answers || []).map(a => new SmartSequenceAnswerEdited(a));
  }

  static validate(data: SmartSequenceQuestionEdited, sequenceIndex: number, questionIndex: number): ProductError[] {
    const errors: ProductError[] = ProductError.validatePrimitiveTypes(data, ['text']) //
      .map(error => ({ ...error, payload: { sequenceIndex, questionIndex } }));

    if (!data.answers || data.answers.length < 1) {
      errors.push({ type: ProductErrorType.NOT_ENOUGH_VALUE, property: 'answers', payload: { sequenceIndex } });
    }

    const contentError = Content.validate(data.content, 0).map(error => {
      error.property = 'content';
      error.payload.questionIndex = questionIndex;
      error.payload.sequenceIndex = sequenceIndex;
      return error;
    });

    if (data.weight < 1) {
      errors.push({
        type: ProductErrorType.OUT_OF_RANGE,
        property: 'weight',
        payload: { sequenceIndex, questionIndex },
      });
    }

    data.answers
      .map((a, answerIndex) => SmartSequenceAnswerEdited.validate(a, sequenceIndex, questionIndex, answerIndex))
      .forEach(subArray => errors.push(...subArray));

    return [...errors, ...contentError];
  }
}

export class SmartSequenceAnswerEdited {
  uuid: string;
  text: string;
  explanation?: string;
  score?: number;
  questionPairedUuid: string;

  constructor(answer?: SmartSequenceAnswerEdited) {
    this.uuid = answer?.uuid;
    this.text = answer?.text;
    this.explanation = answer?.explanation;
    this.score = answer?.score;
    this.questionPairedUuid = answer?.questionPairedUuid;
  }

  static validate(
    data: SmartSequenceAnswerEdited,
    sequenceIndex: number,
    questionIndex: number,
    answerIndex: number,
  ): ProductError[] {
    return ProductError.validatePrimitiveTypes(data, ['text']).map(error => ({
      ...error,
      payload: { sequenceIndex, questionIndex, answerIndex },
    }));
  }
}

export class SmartSequenceParamsEdited {
  title: string;
  description?: string;
  objectives?: string;
  rules?: string;
  local: string;
  author: string;
  recommendedDuration?: number;
  canReplay?: boolean;
  totalNumberReplay?: number;
  endMessage?: string;
  failEndMessage: string;
  successEndMessage: string;
  hasValidationScore: boolean;
  validationScore: number;
  randomizeAnswers: boolean;
  thresholds: {
    first: number;
    second: number;
    // third: number;
  };
  graphicConfig?: SmartSequenceStyleParams;

  constructor(params?: SmartSequenceParamsEdited) {
    this.title = params?.title ?? null;
    this.description = params?.description ?? null;
    this.objectives = params?.objectives ?? null;
    this.graphicConfig = new SmartSequenceStyleParams(params?.graphicConfig);
    this.rules = params?.rules ?? null;
    this.author = params?.author ?? null;
    this.local = params?.local ?? null;
    this.recommendedDuration = params?.recommendedDuration ?? null;
    this.canReplay = params?.canReplay ?? false;
    this.totalNumberReplay = params?.totalNumberReplay ?? null;
    this.endMessage = params?.endMessage ?? null;
    this.randomizeAnswers = params?.randomizeAnswers ?? false;
    this.hasValidationScore = params?.hasValidationScore ?? false;
    this.validationScore = params?.validationScore ?? 0;
    this.failEndMessage = params?.failEndMessage || null;
    this.successEndMessage = params?.successEndMessage || null;
    this.thresholds = params?.thresholds ?? {
      first: 30,
      second: 50,
      // third: 80,
    };
  }

  static validate(data: SmartSequenceParamsEdited): ProductError[] {
    const errors = ProductError.validatePrimitiveTypes(data, ['title', 'local', 'author']); //
    errors.push(...SmartSequenceParamsEdited.checkTresholds(data.thresholds));

    return errors;
  }

  static checkTresholds(data: { first: number; second: number }): ProductError[] {
    //; third: number
    const errors: ProductError[] = [];

    for (const [key, value] of Object.entries(data)) {
      if (value < 0) {
        errors.push({
          type: ProductErrorType.OUT_OF_RANGE,
          property: `thresholds.${key}`,
        });
      }
      if (value > 100) {
        errors.push({
          type: ProductErrorType.OUT_OF_RANGE,
          property: `thresholds.${key}`,
        });
      }
    }
    if (data.first >= data.second) {
      // || data.first >= data.third
      errors.push({
        type: ProductErrorType.BAD_FORMAT,
        property: `thresholds.first`,
      });
    }
    // if (data.second >= data.third) {
    //   errors.push({
    //     type: ProductErrorType.BAD_FORMAT,
    //     property: `thresholds.second`,
    //   });
    // }
    return errors;
  }
}

export class SmartSequenceEditionInfo {
  paramsStatus: ProductMenuEditionStatus;
  contentStatus: ProductMenuEditionStatus;
  validationStatus: ProductMenuEditionStatus;
  publicationStatus: ProductMenuEditionStatus;
  ssoLinks: SSOLink[];
  validationStep: ProductMenuValidationStep;

  constructor(info?: SmartSequenceEditionInfo) {
    this.paramsStatus = menuEditionStatusFactory(info?.paramsStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.contentStatus = menuEditionStatusFactory(info?.contentStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.validationStatus = menuEditionStatusFactory(info?.validationStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.publicationStatus = menuEditionStatusFactory(info?.publicationStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.ssoLinks = (info?.ssoLinks || []).map(link => new SSOLink(link));
    this.validationStep = info?.validationStep ?? ProductMenuValidationStep.CONDITIONS;
  }
}
